import React, { Suspense } from 'react';
import { colors } from '@/styles';
import { isPlaceholderItem, PLACEHOLDER_ITEM_STATUS } from '@/utilities/forwardStocking';
import {
    OUTBOUND_MANIFEST_TYPES,
    PALLET_STATUSES,
    STAGED_STATUSES,
    STORED_STATUSES,
    RECEIVED_STATUSES,
    MANIFEST_STATUSES,
} from '@/constants/manifestStatuses';
import {
    hasOpenException,
    getItemIdDisplay,
    getSerialDisplay,
    getBarcodeDisplay,
    getPalletDisplay,
    getLocationDisplay,
    getSkuDescription,
    getOrderPallets,
    orderHasStaged,
    formatQty,
    allPlaceholdersSelected,
    PLACEHOLDER_PRINT_BLOCKED,
} from '../utils';
import { pickBlockedReason, collectPalletIds } from '../pickBlockers';
import { getItemPalletId } from '@/utilities/manifestPallets';

// Lazy so this config stays free of the Apollo/firebase/UserContext chain the dropdown needs (the grid
// configs are imported by plain unit tests, which can't load App.js).
const StagingVerifierSelect = React.lazy(() => import('../../StagingVerifierSelect'));
const PickerSelect = React.lazy(() => import('../../PickerSelect'));

// Parent segmented bar / progress track staged vs not (the manifest's staging progress).
const CATEGORIES = [
    { key: 'STAGED', label: 'Staged', color: colors.greens.primary },
    { key: 'AWAITING', label: 'Awaiting Staging', color: colors.oranges.primary },
    { key: 'EXCEPTION', label: 'Exception', color: colors.reds.primary },
];

const classify = (item) => {
    if (hasOpenException(item)) return 'EXCEPTION';
    if (STAGED_STATUSES.includes(item?.pallet?.warehouse_status)) return 'STAGED';
    return 'AWAITING';
};

const isStored = (item) =>
    STORED_STATUSES.includes(item?.pallet?.warehouse_status) && !STAGED_STATUSES.includes(item?.pallet?.warehouse_status);

// Pick & Stage needs a pallet to move and not already staged; being stored is not required.
const canStage = (item) => !!item?.pallet?.pallet_id && !STAGED_STATUSES.includes(item?.pallet?.warehouse_status);
// An item can be (re)stored to a location until it has been staged — palletless items included
// (the store flow gives them their own pallet).
const canStore = (item) => !STAGED_STATUSES.includes(item?.pallet?.warehouse_status);

// Web can neither allocate forward stocked items nor split a shared pallet, so both are blocked here
// and sent to the mobile app. See ../pickBlockers.
const itemBlockedReason = (blockers, item) =>
    pickBlockedReason(blockers, { orderId: item?.order_id, palletIds: [getItemPalletId(item)] });

const orderBlockedReason = (blockers, order) =>
    pickBlockedReason(blockers, { orderId: order?.order_id, palletIds: collectPalletIds([order]) });

const selectionBlockedReason = (blockers, selected = []) => {
    const blocked = selected.map((item) => itemBlockedReason(blockers, item)).filter(Boolean);
    if (!blocked.length) return '';
    const scope = selected.length > 1 ? `${blocked.length} of ${selected.length} selected items: ` : '';
    return `${scope}${blocked[0]}`;
};

// Every unit on the manifest is staged — gate for WILL_CALL "Complete Pickup".
const allUnitsStaged = (manifest) => {
    const orders = manifest?.orders || [];
    return (
        orders.length > 0 &&
        orders.every((order) =>
            (order.manifestItems || []).every((item) => STAGED_STATUSES.includes(item?.pallet?.warehouse_status))
        )
    );
};

// Granular per-item status so the outbound flow (received → stored → staged) is legible.
const childStatus = (item, order) => {
    if (isPlaceholderItem(item, order)) return PLACEHOLDER_ITEM_STATUS;
    if (hasOpenException(item)) return { label: 'Exception', color: colors.reds.primary };
    const status = item?.pallet?.warehouse_status;
    if (STAGED_STATUSES.includes(status)) return { label: 'Staged', color: colors.greens.primary };
    if (status === PALLET_STATUSES.PICKED) return { label: 'Picked', color: colors.blues.primary };
    if (isStored(item)) return { label: 'Stored', color: colors.blues.primary };
    if (RECEIVED_STATUSES.includes(status)) return { label: 'Received', color: colors.oranges.primary };
    return { label: 'Not Received', color: colors.oranges.primary };
};

export const outboundConfig = {
    statusModel: {
        categories: CATEGORIES,
        classify,
        chip: (counts, qty) => {
            if (counts.EXCEPTION > 0) return { label: 'Exception', key: 'EXCEPTION' };
            if (counts.AWAITING === 0 && qty > 0) return { label: 'Staged', key: 'STAGED' };
            return { label: `${counts.STAGED} / ${qty}`, key: 'AWAITING' };
        },
        statusText: (counts, qty) => {
            if (counts.EXCEPTION > 0) {
                return `${counts.EXCEPTION} exception${counts.EXCEPTION > 1 ? 's' : ''} · ${counts.STAGED}/${qty}`;
            }
            if (counts.AWAITING > 0) return `${counts.STAGED}/${qty} staged`;
            return `All ${qty} staged`;
        },
    },

    legend: CATEGORIES,

    // Header progress bars (first entry drives the header status pill / deriveStatus).
    progress: [{ key: 'staged', category: 'STAGED', label: 'Staged Progress', verb: 'staged' }],

    deriveStatus: ({ manifest, inProgress, total }) => {
        if (['inProgress', 'complete'].includes(manifest?.route?.status)) return MANIFEST_STATUSES.COMPLETE;
        if (total > 0 && inProgress >= total) return MANIFEST_STATUSES.STAGED;
        if (inProgress > 0) return MANIFEST_STATUSES.IN_PROGRESS;
        return MANIFEST_STATUSES.AWAITING_STAGING;
    },

    // The verifier dropdown only renders when the page is editable (owning client and its teammates);
    // partner viewers on another client's manifest don't see it.
    extraHeaderFields: (manifest, { editable = false, callbacks = {} } = {}) => [
        { label: 'Staging Zone', get: (m) => m.staging_zone || 'Not set' },
        {
            label: 'Staging Verifier',
            get: (m) => (
                <Suspense fallback={null}>
                    <StagingVerifierSelect manifest={m} onError={callbacks.onError} />
                </Suspense>
            ),
            visible: (m) => editable && m.type === 'OUTBOUND' && !!m.route?.needs_verification,
        },
        {
            label: 'Picker',
            get: (m) => (
                <Suspense fallback={null}>
                    <PickerSelect manifest={m} onError={callbacks.onError} />
                </Suspense>
            ),
            visible: (m) => editable && OUTBOUND_MANIFEST_TYPES.includes(m.type),
        },
    ],

    childColumns: [
        { key: 'sku', header: 'SKU / Description', width: '1.3fr', variant: 'text', sortable: true, filterable: true, get: getSkuDescription },
        { key: 'qty', header: 'Qty', width: '0.4fr', variant: 'text', sortable: true, filterable: true, get: (item) => formatQty(item.quantity ?? 1) },
        { key: 'itemId', header: 'Line ID', width: '0.9fr', variant: 'id', sortable: true, filterable: true, get: getItemIdDisplay },
        { key: 'serial', header: 'Serial', width: '0.95fr', variant: 'text', sortable: true, filterable: true, get: getSerialDisplay },
        { key: 'barcode', header: 'Barcode / QR', width: '1fr', variant: 'text', sortable: true, filterable: true, get: getBarcodeDisplay },
        { key: 'pallet', header: 'Pallet ID', width: '0.85fr', variant: 'id', sortable: true, filterable: true, get: (item) => getPalletDisplay(item.pallet) || '—' },
        { key: 'location', header: 'Location', width: '0.75fr', variant: 'text', sortable: true, filterable: true, get: getLocationDisplay },
        { key: 'status', header: 'Status', width: '0.95fr', variant: 'pill', sortable: true, filterable: true, get: childStatus, getValue: (item, order) => childStatus(item, order).label },
    ],

    rowButtons: (actions, manifest, blockers) => [
        {
            key: 'assign',
            label: '⌒ Assign location',
            // Received (and not yet staged) only — assigning a location never receives an item.
            visible: ({ child }) => RECEIVED_STATUSES.includes(child?.pallet?.warehouse_status) && canStore(child),
            onClick: ({ child }) => actions.assignLocation([child]),
        },
        {
            key: 'rename',
            label: '✎ Rename pallet',
            visible: ({ child }) => !!child?.pallet,
            onClick: ({ child }) => actions.renamePallet(child),
        },
        {
            key: 'history',
            label: '🕘 Pallet history',
            visible: ({ child }) => !!child?.pallet?.pallet_id,
            onClick: ({ child }) => actions.palletLog(child.pallet),
        },
        {
            key: 'stage',
            label: '⇉ Pick & Stage',
            // Staging zone is optional; only requires the item be stored to a location.
            disabled: ({ child }) => !canStage(child) || !!itemBlockedReason(blockers, child),
            disabledReason: ({ child }) => itemBlockedReason(blockers, child),
            onClick: ({ child }) => actions.pickStage([child]),
        },
        {
            key: 'resolve',
            label: '✓ Resolve exception',
            color: colors.greens.primary,
            visible: ({ child }) => hasOpenException(child),
            onClick: ({ row, child }) => actions.resolveException({ order: row, item: child }),
        },
    ],

    parentButtons: (actions, manifest, blockers) => [
        // No order-level "Assign location" — assigning a whole order fanned every line item into the
        // multi-input Store dialog (one input per pallet). Assignment stays row-level (single item) and
        // bulk-select, matching inbound.
        {
            key: 'stage',
            label: '⇉ Pick & Stage',
            disabled: ({ row }) => !!orderBlockedReason(blockers, row),
            disabledReason: ({ row }) => orderBlockedReason(blockers, row),
            onClick: ({ row }) => actions.pickStage(row.manifestItems || []),
        },
        {
            key: 'history',
            label: '🕘 Pallet history',
            visible: ({ row }) => getOrderPallets(row.manifestItems || []).length === 1,
            onClick: ({ row }) => actions.palletLog(getOrderPallets(row.manifestItems || [])[0]),
        },
        {
            key: 'unstage',
            label: '⤺ Un-stage order',
            color: colors.reds.primary,
            visible: ({ row }) => orderHasStaged(row),
            onClick: ({ row }) => actions.unstage(row),
        },
    ],

    bulkButtons: (actions, manifest, blockers) => [
        // WILL_CALL only: appears (no selection needed) once every unit is staged.
        {
            key: 'complete-pickup',
            label: '✓ Complete Pickup',
            variant: 'primary',
            visible: ({ subject }) =>
                subject?.type === 'WILL_CALL' &&
                subject?.status !== MANIFEST_STATUSES.COMPLETE &&
                allUnitsStaged(subject),
            onClick: ({ subject }) => actions.completePickup(subject.orders || []),
        },
        // Persistent: set staging zone (optional; offered until one is set — no selection needed).
        {
            key: 'setzone',
            label: '⌘ Set staging zone',
            variant: 'outlined',
            color: colors.blues.primary,
            visible: () => !manifest?.staging_zone,
            onClick: () => actions.setStagingZone(),
        },
        {
            key: 'stage',
            label: '⇉ Pick & Stage',
            variant: 'primary',
            visible: ({ selected }) => selected.length > 0,
            // Blocked items disable the whole action rather than being skipped — the skip is what made
            // half-staging an order invisible.
            disabled: ({ selected }) => !selected.some(canStage) || !!selectionBlockedReason(blockers, selected),
            disabledReason: ({ selected }) => selectionBlockedReason(blockers, selected),
            onClick: ({ selected }) => actions.pickStage(selected),
        },
        {
            key: 'assign',
            label: '⌒ Assign location',
            variant: 'outlined',
            color: colors.blues.primary,
            // Same gate as the row action — not-received items are filtered out by actions.assignLocation.
            visible: ({ selected }) =>
                selected.some((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status) && canStore(item)),
            onClick: ({ selected }) => actions.assignLocation(selected),
        },
        {
            key: 'print-item-labels',
            label: '🖨 Print item labels',
            variant: 'outlined',
            visible: ({ selected }) => selected.length > 0,
            disabled: allPlaceholdersSelected,
            disabledReason: (ctx) => (allPlaceholdersSelected(ctx) ? PLACEHOLDER_PRINT_BLOCKED : ''),
            onClick: ({ selected }) => actions.printItemLabels(selected),
        },
        {
            key: 'print-pallet-labels',
            label: '🖨 Print pallet labels',
            variant: 'outlined',
            // Only palletized items have a pallet label to print.
            visible: ({ selected }) => selected.some((item) => item?.pallet?.pallet_id),
            onClick: ({ selected }) => actions.printPalletLabels(selected),
        },
    ],
};
